// uncomment the line below if you want to activate why-did-you-render
// import './wdyr';
import 'core-js';
import 'regenerator-runtime/runtime';
import 'styles/main.scss';
import './i18n';

import { datadogLogs } from '@datadog/browser-logs';
import * as Sentry from '@sentry/browser';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import {
    AG_GRID_KEY,
    DATADOG_CLIENT_TOKEN,
    FLAVOUR,
    IS_E2E,
    IS_PREPROD,
    IS_PRESENTATION,
    IS_PROD,
    IS_STAGING,
    SENTRY_DSN,
    VERSION,
} from 'env';
import { installExportFilenameInterceptor } from 'helpers/exportHelpers';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router/dom';
import { getClientBind } from 'store/APIClient';
import { router } from 'store/router';
import store from 'store/store';

LicenseManager.setLicenseKey(AG_GRID_KEY);
ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

// Prefix every anchor-based download with the current staging/local name (no-op on prod).
installExportFilenameInterceptor();

const getReportingEnvironment = () => {
    // Running Playwright in CI sets IS_PROD to true, so we need to check it first to avoid
    // sending test errors to production Sentry and scaring the team watching Slack alerts.
    if (IS_E2E) return 'staging';
    if (IS_PROD) return 'production';
    if (IS_PRESENTATION) return 'presentation';
    if (IS_PREPROD) return 'preprod';
    return 'staging';
};

// The browser SDK never sets the reserved `host` attribute on logs; align it with the infra
// host name used in Datadog so browser logs group with their environment.
const getReportingHost = () => {
    if (IS_E2E) return 'staging-vision';
    if (IS_PROD) return 'production';
    if (IS_PRESENTATION) return 'presentation';
    if (IS_PREPROD) return 'preprod';
    return 'staging-vision';
};

// Setup Sentry for exception monitoring and datadog for logging
if (IS_STAGING || IS_PROD || IS_PRESENTATION || IS_PREPROD) {
    Sentry.init({
        dsn: SENTRY_DSN,
        environment: getReportingEnvironment(),
        release: VERSION,
    });
    Sentry.setTag('flavour', FLAVOUR);
    Sentry.setTag('bind', getClientBind());

    datadogLogs.init({
        clientToken: DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: 'vision-front',
        env: getReportingEnvironment(),
        version: VERSION,
        forwardErrorsToLogs: false,
        forwardConsoleLogs: [],
        sessionSampleRate: 100,
        beforeSend: (event) => {
            event.host = getReportingHost();
            return true;
        },
    });
}

const MOUNT_NODE = document.getElementById('root');
if (!MOUNT_NODE) {
    throw new Error('Root element not found');
}

createRoot(MOUNT_NODE).render(
    <StrictMode>
        <Provider store={store}>
            <NuqsAdapter>
                <RouterProvider router={router} />
            </NuqsAdapter>
        </Provider>
    </StrictMode>,
);
